import { loadAcademicYearSettingsCache, loadAllCalendarDaysLocalCaches, loadCalendarDaysLocalCache } from '../services/calendarService';
import { loadAcademicCalendarSectionsCache } from '../services/sectionLayoutService';
import type { OffDayByDate } from './annualProgressDataCache';
import type { CalendarDay, DayKind } from '../types/domain';
import {
  readAcademicSectionCalendarDaysFromLocalStorage,
  type AcademicSectionCalendarSettings,
} from './calendarSectionAcademicYear';

function normalizeDayIso(day: string | null | undefined): string {
  const s = (day ?? '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function addHolidayEntries(
  set: Set<string>,
  noteByIso: Record<string, string>,
  days: CalendarDay[] | null | undefined
): void {
  for (const d of days ?? []) {
    if (d.day_kind !== 'holiday' && d.day_kind !== 'closure' && d.day_kind !== 'vacation') continue;
    const iso = normalizeDayIso(d.day);
    if (!iso) continue;
    set.add(iso);
    const note = (d.note ?? '').trim();
    if (note && !noteByIso[iso]) noteByIso[iso] = note;
  }
}

function addDayKindEntries(
  dayKindByIso: Record<string, DayKind>,
  days: CalendarDay[] | null | undefined
): void {
  for (const d of days ?? []) {
    const iso = normalizeDayIso(d.day);
    if (!iso) continue;
    if (!dayKindByIso[iso]) dayKindByIso[iso] = d.day_kind;
  }
}

export type AcademicCalendarHolidayCache = {
  isoSet: Set<string>;
  noteByIso: Record<string, string>;
  /** 학사일정에 등록된 날짜별 day_kind (토·일 수업일 class 등) */
  dayKindByIso: Record<string, DayKind>;
  sectionSettings: AcademicSectionCalendarSettings | null;
  yearStartsOn: string;
  yearEndsOn: string;
};

/**
 * 학사일정 LS 캐시(섹션 JSON·섹션별 공휴일·base calendar_days)에서
 * 공휴일·휴업일 ISO와 이름, day_kind·방학 설정을 모은다. 추가 DB 조회 없음.
 */
export function collectAcademicCalendarHolidayCache(
  workspaceId: string,
  gradeId: string
): AcademicCalendarHolidayCache {
  const isoSet = new Set<string>();
  const noteByIso: Record<string, string> = {};
  const dayKindByIso: Record<string, DayKind> = {};
  const ws = workspaceId.trim();
  if (!ws) {
    return {
      isoSet,
      noteByIso,
      dayKindByIso,
      sectionSettings: null,
      yearStartsOn: '',
      yearEndsOn: '',
    };
  }

  const sections = loadAcademicCalendarSectionsCache(ws) ?? [];
  const gid = gradeId.trim();
  const matched = gid
    ? sections.find((s) => Array.isArray(s.gradeIds) && s.gradeIds.includes(gid))
    : undefined;
  const sectionId = matched?.id ?? 'base';
  const section = matched ?? sections.find((s) => s.id === sectionId);

  const ingest = (days: CalendarDay[] | null | undefined) => {
    addHolidayEntries(isoSet, noteByIso, days);
    addDayKindEntries(dayKindByIso, days);
  };

  ingest(readAcademicSectionCalendarDaysFromLocalStorage(ws, sectionId));
  ingest(section?.academicCalendarDays);

  let sectionSettings: AcademicSectionCalendarSettings | null =
    section?.academicCalendarSettings ?? null;
  let yearStartsOn = (sectionSettings?.yearStartsOn ?? '').trim();
  let yearEndsOn = (sectionSettings?.yearEndsOn ?? '').trim();

  if (sectionId === 'base') {
    const baseSec = sections.find((s) => s.id === 'base') ?? section;
    ingest(baseSec?.academicCalendarDays);
    if (!sectionSettings) sectionSettings = baseSec?.academicCalendarSettings ?? null;

    const year =
      baseSec?.academicCalendarSettings?.academicYear && baseSec.academicCalendarSettings.academicYear > 0
        ? baseSec.academicCalendarSettings.academicYear
        : new Date().getFullYear();
    const settings = loadAcademicYearSettingsCache(ws, year) ?? loadAcademicYearSettingsCache(ws, year - 1);
    const from = (settings?.year_starts_on || baseSec?.academicCalendarSettings?.yearStartsOn || '').trim();
    const to = (settings?.year_ends_on || baseSec?.academicCalendarSettings?.yearEndsOn || '').trim();
    if (from && to) {
      ingest(loadCalendarDaysLocalCache(ws, from, to).rows);
      if (!yearStartsOn) yearStartsOn = from;
      if (!yearEndsOn) yearEndsOn = to;
    }
    ingest(loadAllCalendarDaysLocalCaches(ws));
    if (!sectionSettings && baseSec?.academicCalendarSettings) {
      sectionSettings = baseSec.academicCalendarSettings;
    }
  }

  if (!yearStartsOn) yearStartsOn = (sectionSettings?.yearStartsOn ?? '').trim();
  if (!yearEndsOn) yearEndsOn = (sectionSettings?.yearEndsOn ?? '').trim();

  return {
    isoSet,
    noteByIso,
    dayKindByIso,
    sectionSettings,
    yearStartsOn,
    yearEndsOn,
  };
}

export type AcademicTermDateRange = {
  starts_on: string;
  ends_on: string;
};

/** 1·2학기 구간(starts_on~ends_on)에 포함되는지 */
export function isIsoInAcademicTerms(
  iso: string | null | undefined,
  terms: ReadonlyArray<AcademicTermDateRange> | null | undefined
): boolean {
  const day = (iso ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  const list = terms ?? [];
  if (list.length === 0) return true;
  return list.some((t) => {
    const from = (t.starts_on ?? '').trim().slice(0, 10);
    const to = (t.ends_on ?? '').trim().slice(0, 10);
    return !!from && !!to && day >= from && day <= to;
  });
}

/**
 * 월 그리드 흐림 플래그.
 * - 기본: 학기 밖 ISO
 * - 이달 앞쪽이 연속 흐림(1일부터)이면 앞쪽 빈 칸도 흐림
 * - 이달 뒤쪽이 연속 흐림(말일까지)이면 뒤쪽 빈 칸도 흐림
 */
export function buildMonthOutOfTermFlags(
  cells: ReadonlyArray<{ day: number | null; iso: string | null }>,
  terms: ReadonlyArray<AcademicTermDateRange> | null | undefined
): boolean[] {
  const base = cells.map((c) => !!c.iso && !isIsoInAcademicTerms(c.iso, terms));
  const out = base.slice();

  let firstIn = -1;
  let lastIn = -1;
  for (let i = 0; i < cells.length; i += 1) {
    if (cells[i]?.day != null) {
      if (firstIn < 0) firstIn = i;
      lastIn = i;
    }
  }
  if (firstIn < 0 || lastIn < 0) return out;

  /* 접두 흐림 블럭: 1일부터 연속 학기 밖 */
  if (base[firstIn]) {
    for (let i = 0; i < firstIn; i += 1) out[i] = true;
  }

  /* 접미 흐림 블럭: 말일까지 연속 학기 밖 */
  if (base[lastIn]) {
    for (let i = lastIn + 1; i < cells.length; i += 1) out[i] = true;
  }

  return out;
}

type TermOffDayFields = {
  holidayDays: string[];
  offDayByDate: OffDayByDate;
  dayKindByDate: Record<string, DayKind>;
};

/**
 * 학기 슬롯 데이터의 공휴·휴업·방학 필드에 학사일정 LS 캐시를 병합(네트워크 없음).
 * 기존 값이 우선이며, 비어 있는 날짜만 캐시로 채운다.
 */
export function withAcademicCalendarOffDaysFromLocalCache<T extends TermOffDayFields>(
  term: T,
  workspaceId: string,
  gradeId: string
): T {
  const cache = collectAcademicCalendarHolidayCache(workspaceId, gradeId);
  if (cache.isoSet.size === 0 && Object.keys(cache.dayKindByIso).length === 0) return term;

  const holidaySet = new Set(term.holidayDays);
  const offDayByDate: OffDayByDate = { ...term.offDayByDate };
  const dayKindByDate: Record<string, DayKind> = { ...cache.dayKindByIso, ...term.dayKindByDate };
  cache.isoSet.forEach((iso) => {
    holidaySet.add(iso);
    if (offDayByDate[iso]) return;
    const kind = cache.dayKindByIso[iso];
    const note = cache.noteByIso[iso];
    const offKind: DayKind = kind === 'closure' || kind === 'vacation' ? kind : 'holiday';
    offDayByDate[iso] = note ? { kind: offKind, note } : { kind: offKind };
  });
  return {
    ...term,
    holidayDays: Array.from(holidaySet),
    offDayByDate,
    dayKindByDate,
  };
}

/** @deprecated `collectAcademicCalendarHolidayCache` 사용 */
export function collectAcademicCalendarHolidayIsoSet(workspaceId: string, gradeId: string): Set<string> {
  return collectAcademicCalendarHolidayCache(workspaceId, gradeId).isoSet;
}

/**
 * 대시보드 RQ 번들의 학기별 휴일·휴업·방학을 LS 캐시에 병합.
 * 위클리/캘린더 헤더는 LS만 보면 데일리(번들)와 어긋날 수 있어 번들을 우선 합친다.
 */
export function mergeOffDaysIntoHolidayCache(
  base: AcademicCalendarHolidayCache,
  sources: Array<{ holidayDays?: string[] | null; offDayByDate?: OffDayByDate | null } | null | undefined>
): AcademicCalendarHolidayCache {
  const isoSet = new Set(base.isoSet);
  const noteByIso: Record<string, string> = { ...base.noteByIso };
  const dayKindByIso: Record<string, DayKind> = { ...base.dayKindByIso };
  for (const src of sources) {
    if (!src) continue;
    for (const isoRaw of src.holidayDays ?? []) {
      const iso = normalizeDayIso(isoRaw);
      if (!iso) continue;
      isoSet.add(iso);
      if (!dayKindByIso[iso]) dayKindByIso[iso] = 'holiday';
    }
    const off = src.offDayByDate ?? {};
    for (const [isoRaw, row] of Object.entries(off)) {
      const iso = normalizeDayIso(isoRaw);
      if (!iso) continue;
      if (row?.kind === 'holiday' || row?.kind === 'closure' || row?.kind === 'vacation') {
        isoSet.add(iso);
        if (!dayKindByIso[iso]) dayKindByIso[iso] = row.kind;
        const note = (row.note ?? '').trim();
        if (note && !noteByIso[iso]) noteByIso[iso] = note;
      }
    }
  }
  return {
    isoSet,
    noteByIso,
    dayKindByIso,
    sectionSettings: base.sectionSettings,
    yearStartsOn: base.yearStartsOn,
    yearEndsOn: base.yearEndsOn,
  };
}
